import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface UseScrollRestorationProps {
  elementId: string;
}

/**
 * Custom scroll restoration hook for scrollable containers.
 *
 * React Router DOM's ScrollRestoration only works with window scroll, but our layout uses
 * a fixed-height container with overflow instead of window scrolling. This hook manages
 * scroll positions for a specific DOM element and restores them on navigation.
 *
 * How it works: Tracks scroll position on scroll events, saves to sessionStorage on route
 * change, and restores the saved position when returning to a route.
 *
 * @param elementId - The ID of the scrollable DOM element to manage
 */
export const useScrollRestoration = ({ elementId }: UseScrollRestorationProps) => {
  const location = useLocation();
  const scrollPositions = useRef<Record<string, number>>({}); // key: location pathname, value: scroll top
  const previousLocation = useRef<string | null>(null);

  // Load scroll positions from sessionStorage on mount
  useEffect(() => {
    scrollPositions.current = loadScrollPositionsFromStorage();
  }, []);

  // Continuously save scroll position as user scrolls
  useEffect(() => {
    const scrollableElement = getScrollableElement(elementId);
    if (!scrollableElement) return;

    const handleScroll = () => {
      if (!previousLocation.current) return;

      updateScrollPositionInMemory(scrollableElement, previousLocation.current, scrollPositions.current);
    };

    scrollableElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollableElement.removeEventListener('scroll', handleScroll);

      if (!previousLocation.current) return;

      updateScrollPositionInMemory(scrollableElement, previousLocation.current, scrollPositions.current);

      saveScrollPositionsToStorage(scrollPositions.current);
    };
  }, [elementId]);

  // Handle scroll restoration on location change
  useEffect(() => {
    const scrollableElement = getScrollableElement(elementId);
    if (!scrollableElement) return;

    const locationKey = location.pathname;

    // Don't restore if we're on the same route
    if (previousLocation.current === locationKey) return;

    restoreScrollPosition(scrollableElement, locationKey, scrollPositions.current);
    previousLocation.current = locationKey;
    saveScrollPositionsToStorage(scrollPositions.current);
  }, [location, elementId]);
};

// Helper functions for scroll position management
const loadScrollPositionsFromStorage = (): Record<string, number> => {
  try {
    const storedPositions = sessionStorage.getItem('scrollPositions');
    if (!storedPositions) return {};

    const parsed = JSON.parse(storedPositions);
    if (!parsed || typeof parsed !== 'object') return {};

    const validatedPositions: Record<string, number> = {};
    for (const key of Object.keys(parsed)) {
      const value = parsed[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        validatedPositions[key] = value;
      }
    }

    return validatedPositions;
  } catch {
    return {};
  }
};

const saveScrollPositionsToStorage = (positions: Record<string, number>): void => {
  try {
    sessionStorage.setItem('scrollPositions', JSON.stringify(positions));
  } catch (error) {
    console.warn('Failed to save scroll positions to sessionStorage:', error);
  }
};

const getScrollableElement = (elementId: string): HTMLElement | null => {
  const element = document.getElementById(elementId);
  if (!element || typeof element.addEventListener !== 'function') return null;

  return element;
};

const updateScrollPositionInMemory = (
  element: HTMLElement,
  locationKey: string,
  positions: Record<string, number>,
): void => {
  const currentScrollTop = element.scrollTop;
  positions[locationKey] = currentScrollTop;
};

const restoreScrollPosition = (element: HTMLElement, locationKey: string, positions: Record<string, number>): void => {
  const savedPosition = positions[locationKey];

  if (savedPosition !== undefined) {
    element.scrollTo({ top: savedPosition, behavior: 'instant' });
    return;
  }

  element.scrollTo({ top: 0, behavior: 'instant' });
};
